import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Alert,
  Checkbox,
  FormControlLabel,
  ListItemButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupsIcon from '@mui/icons-material/Groups';
import CalculateIcon from '@mui/icons-material/Calculate';
import CodeIcon from '@mui/icons-material/Code';
import DownloadIcon from '@mui/icons-material/Download';
import StorageIcon from '@mui/icons-material/Storage';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Import mock data
import cohortsData from '../../data/cohorts.json';
import parametersData from '../../data/parameters.json';
import parameterListsData from '../../data/parameterLists.json';

const DataRequests = () => {
  // State for cohorts and selection
  const [cohorts] = useState(cohortsData.cohorts);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fallzahlschätzung state
  const [estimating, setEstimating] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState(null);

  // SQL Dialog state
  const [sqlDialogOpen, setSqlDialogOpen] = useState(false);
  const [generatedSQL, setGeneratedSQL] = useState('');

  // Datenextraktion state
  const [selectedParameterList, setSelectedParameterList] = useState(null);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [extractionNotes, setExtractionNotes] = useState('');

  // Success messages
  const [successMessage, setSuccessMessage] = useState('');

  // Filter cohorts by search term
  const filteredCohorts = cohorts.filter(cohort =>
    cohort.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cohort.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get available parameters from selected parameter list
  const getAvailableParameters = () => {
    if (!selectedParameterList) return [];
    return parametersData.parameters.filter(p =>
      selectedParameterList.parameterIds.includes(p.id)
    );
  };

  // Handle cohort selection
  const handleCohortSelect = (cohort) => {
    setSelectedCohort(cohort);
    setEstimatedCount(null);
    setExtractionNotes('');
    setSelectedParameterList(null);
    setSelectedParameters([]);
  };

  // Handle parameter list selection
  const handleParameterListSelect = (listId) => {
    const list = parameterListsData.lists.find(l => l.id === listId);
    setSelectedParameterList(list);

    // Pre-select all parameters from this list
    if (list) {
      const allParams = parametersData.parameters.filter(p =>
        list.parameterIds.includes(p.id)
      );
      setSelectedParameters(allParams);
    } else {
      setSelectedParameters([]);
    }
  };

  // Handle Fallzahlschätzung
  const handleEstimate = () => {
    setEstimating(true);
    // Simulate API call
    setTimeout(() => {
      setEstimatedCount(selectedCohort.patientCount);
      setEstimating(false);
      setSuccessMessage('Fallzahlschätzung erfolgreich durchgeführt');
      setTimeout(() => setSuccessMessage(''), 3000);
    }, 1500);
  };

  // Generate SQL for cohort
  const generateCohortSQL = () => {
    if (!selectedCohort) return '';

    const whereConditions = selectedCohort.includeConditions.map(c => {
      const paramName = c.parameterName.toLowerCase().replace(/\s+/g, '_');
      return `  AND ${paramName} ${c.operator} ${c.value}`;
    }).join('\n');

    const excludeConditions = selectedCohort.excludeConditions.length > 0
      ? '\n' + selectedCohort.excludeConditions.map(c => {
          const paramName = c.parameterName.toLowerCase().replace(/\s+/g, '_');
          return `  AND NOT (${paramName} ${c.operator} ${c.value})`;
        }).join('\n')
      : '';

    return `-- Fallzahlschätzung für: ${selectedCohort.name}
-- Kohorten-ID: ${selectedCohort.id}
-- Erstellt: ${new Date().toLocaleString()}

SELECT
  COUNT(DISTINCT person_id) as patient_count
FROM health_data_lake.monitoring_data
WHERE measurement_date >= '2023-01-01'
  AND measurement_date <= '2024-12-31'
${whereConditions}${excludeConditions};`;
  };

  // Handle SQL anzeigen
  const handleShowSQL = () => {
    const sql = generateCohortSQL();
    setGeneratedSQL(sql);
    setSqlDialogOpen(true);
  };

  // Copy SQL to clipboard
  const handleCopySQL = () => {
    navigator.clipboard.writeText(generatedSQL);
    setSuccessMessage('SQL in Zwischenablage kopiert');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Handle JSON Export
  const handleExportJSON = () => {
    const cohortDefinition = {
      id: selectedCohort.id,
      name: selectedCohort.name,
      description: selectedCohort.description,
      parameterListId: selectedCohort.parameterListId,
      parameterListName: selectedCohort.parameterListName,
      includeConditions: selectedCohort.includeConditions,
      excludeConditions: selectedCohort.excludeConditions,
      estimatedPatients: selectedCohort.patientCount,
      exportedAt: new Date().toISOString()
    };

    const dataStr = JSON.stringify(cohortDefinition, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kohorte_${selectedCohort.name.replace(/\s+/g, '_').toLowerCase()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSuccessMessage('Kohortendefinition als JSON exportiert');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Toggle parameter selection
  const toggleParameter = (parameter) => {
    if (selectedParameters.find(p => p.id === parameter.id)) {
      setSelectedParameters(selectedParameters.filter(p => p.id !== parameter.id));
    } else {
      setSelectedParameters([...selectedParameters, parameter]);
    }
  };

  // Select/Deselect all parameters
  const handleSelectAll = () => {
    const allParams = getAvailableParameters();
    setSelectedParameters(allParams);
  };

  const handleDeselectAll = () => {
    setSelectedParameters([]);
  };

  // Generate extraction SQL
  const generateExtractionSQL = () => {
    if (!selectedCohort || selectedParameters.length === 0) return '';

    const columnNames = selectedParameters.map(p =>
      `  ${p.name.toLowerCase().replace(/\s+/g, '_')}`
    ).join(',\n');

    const whereConditions = selectedCohort.includeConditions.map(c => {
      const paramName = c.parameterName.toLowerCase().replace(/\s+/g, '_');
      return `  AND ${paramName} ${c.operator} ${c.value}`;
    }).join('\n');

    const excludeConditions = selectedCohort.excludeConditions.length > 0
      ? '\n' + selectedCohort.excludeConditions.map(c => {
          const paramName = c.parameterName.toLowerCase().replace(/\s+/g, '_');
          return `  AND NOT (${paramName} ${c.operator} ${c.value})`;
        }).join('\n')
      : '';

    return `-- Datenextraktion für: ${selectedCohort.name}
-- Kohorten-ID: ${selectedCohort.id}
-- Parameter: ${selectedParameters.length}
-- Erstellt: ${new Date().toLocaleString()}

SELECT
  person_id,
  measurement_date,
${columnNames}
FROM health_data_lake.monitoring_data
WHERE measurement_date >= '2023-01-01'
  AND measurement_date <= '2024-12-31'
${whereConditions}${excludeConditions}
ORDER BY person_id, measurement_date;`;
  };

  // Handle Datenextraktion submission
  const handleSubmitExtraction = () => {
    const extractionSQL = generateExtractionSQL();

    console.log('Datenextraktion angefordert:', {
      cohort: selectedCohort.name,
      parameters: selectedParameters.map(p => p.name),
      sql: extractionSQL,
      notes: extractionNotes
    });

    setSuccessMessage(`Datenextraktion für "${selectedCohort.name}" mit ${selectedParameters.length} Parametern erfolgreich eingereicht!`);

    // Reset after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
      setExtractionNotes('');
    }, 3000);
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            <AssignmentIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 40 }} />
            Datenanfragen
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Fallzahlschätzung und Datenextraktion für Kohorten
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Left Column - Cohorts List */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Kohorten
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TextField
                fullWidth
                size="small"
                placeholder="Kohorten durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <List sx={{ flex: 1, overflow: 'auto' }}>
                {filteredCohorts.map((cohort) => (
                  <ListItemButton
                    key={cohort.id}
                    selected={selectedCohort?.id === cohort.id}
                    onClick={() => handleCohortSelect(cohort)}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      border: selectedCohort?.id === cohort.id ? 2 : 1,
                      borderColor: selectedCohort?.id === cohort.id ? 'primary.main' : 'grey.300',
                      '&:hover': {
                        backgroundColor: 'rgba(237, 125, 49, 0.08)',
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight="bold">
                            {cohort.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {cohort.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                            <Chip
                              icon={<GroupsIcon />}
                              label={`${cohort.patientCount} Pat.`}
                              size="small"
                              color="primary"
                            />
                            <Chip
                              label={`${cohort.includeConditions.length} Kriterien`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Right Column - Actions */}
          <Grid item xs={12} md={8}>
            {!selectedCohort ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Keine Kohorte ausgewählt
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Wählen Sie eine Kohorte aus der Liste links aus, um fortzufahren
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Success Message */}
                {successMessage && (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    {successMessage}
                  </Alert>
                )}

                {/* Section 1: Fallzahlschätzung */}
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        1. Fallzahlschätzung
                      </Typography>
                      {estimatedCount && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={`${estimatedCount} Patienten geschätzt`}
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>

                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Ausgewählte Kohorte
                      </Typography>
                      <Typography variant="h6" gutterBottom>
                        {selectedCohort.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {selectedCohort.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${selectedCohort.includeConditions.length} Einschlusskriterien`}
                          size="small"
                          color="success"
                        />
                        <Chip
                          label={`${selectedCohort.excludeConditions.length} Ausschlusskriterien`}
                          size="small"
                          color="error"
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        startIcon={estimating ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
                        onClick={handleEstimate}
                        disabled={estimating}
                      >
                        {estimating ? 'Schätze...' : 'Fallzahl schätzen'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CodeIcon />}
                        onClick={handleShowSQL}
                      >
                        SQL anzeigen
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportJSON}
                      >
                        JSON Export
                      </Button>
                    </Box>

                    {estimatedCount && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Geschätzte Fallzahl:</strong> {estimatedCount} Patienten erfüllen die Kohortenkriterien.
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>

                {/* Section 2: Datenextraktion */}
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" fontWeight="bold">
                        2. Datenextraktion beantragen
                      </Typography>
                      {estimatedCount && (
                        <Chip
                          label="Nach Fallzahlschätzung"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="body2">
                        Wählen Sie zuerst eine Parameterliste aus dem Datenkatalog aus.
                        Alle Parameter der Liste sind standardmäßig ausgewählt und können bei Bedarf abgewählt werden.
                      </Typography>
                    </Alert>

                    {/* Parameter List Selection */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Parameterliste auswählen</InputLabel>
                      <Select
                        value={selectedParameterList?.id || ''}
                        label="Parameterliste auswählen"
                        onChange={(e) => handleParameterListSelect(e.target.value)}
                      >
                        {parameterListsData.lists.filter(list => list.isPublic).map((list) => (
                          <MenuItem key={list.id} value={list.id}>
                            <Box>
                              <Typography variant="body1">{list.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {list.description} • {list.parameterIds.length} Parameter
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedParameterList && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Ausgewählte Liste
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" gutterBottom>
                          {selectedParameterList.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedParameterList.description}
                        </Typography>
                      </Box>
                    )}

                    {/* Parameter Selection */}
                    {selectedParameterList && (
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Parameter auswählen ({selectedParameters.length} von {getAvailableParameters().length})
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" onClick={handleSelectAll}>
                              Alle auswählen
                            </Button>
                            <Button size="small" onClick={handleDeselectAll}>
                              Alle abwählen
                            </Button>
                          </Box>
                        </Box>

                        <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 1 }}>
                          <List dense>
                            {getAvailableParameters().map((param) => {
                              const isSelected = selectedParameters.find(p => p.id === param.id);
                              return (
                                <ListItem key={param.id} disablePadding>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={!!isSelected}
                                        onChange={() => toggleParameter(param)}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2">
                                          {param.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          ({param.category})
                                        </Typography>
                                        {param.omopMapped && (
                                          <Chip label="OMOP" size="small" color="success" sx={{ height: 18 }} />
                                        )}
                                      </Box>
                                    }
                                    sx={{ width: '100%', m: 0 }}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Paper>
                      </Box>
                    )}

                    {/* Notes */}
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Notizen zur Anfrage (optional)"
                      placeholder="z.B. Verwendungszweck, Projektname, ethische Genehmigung..."
                      value={extractionNotes}
                      onChange={(e) => setExtractionNotes(e.target.value)}
                      sx={{ mb: 3 }}
                    />

                    {/* Submit Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Die Anfrage wird an das Data Steward Team zur Genehmigung weitergeleitet.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Nach der Genehmigung werden die Daten in einer sicheren Arbeitsumgebung bereitgestellt.
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<SendIcon />}
                        onClick={handleSubmitExtraction}
                        disabled={!selectedParameterList || selectedParameters.length === 0}
                      >
                        Extraktion beantragen
                      </Button>
                    </Box>

                    {!selectedParameterList && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Bitte wählen Sie zuerst eine Parameterliste aus.
                      </Alert>
                    )}
                    {selectedParameterList && selectedParameters.length === 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Bitte wählen Sie mindestens einen Parameter aus.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* SQL Dialog */}
        <Dialog
          open={sqlDialogOpen}
          onClose={() => setSqlDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                SQL für Fallzahlschätzung
              </Typography>
              <IconButton onClick={() => setSqlDialogOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  Dieses SQL-Script schätzt die Anzahl der Patienten für die ausgewählte Kohorte "{selectedCohort?.name}".
                </Typography>
              </Alert>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                SQL Query
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopySQL}
              >
                Kopieren
              </Button>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                backgroundColor: '#f5f5f5',
                maxHeight: 400,
                overflow: 'auto'
              }}
            >
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0
                }}
              >
                {generatedSQL}
              </Typography>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSqlDialogOpen(false)}>
              Schließen
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default DataRequests;
